import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import { BUSINESS_CATEGORIES } from "@/lib/bizCategories";
import {
  Home, Zap, ShoppingCart, Car, ShieldCheck, HeartPulse, CreditCard, PiggyBank,
  Popcorn, Sparkles, PawPrint, Package, Bell, BellRing, Plus, Trash2, Star,
} from "lucide-react";

const CATS = [
  { key: "housing", label: "Housing", full: "Housing", Icon: Home },
  { key: "utilities", label: "Utilities", full: "Utilities", Icon: Zap },
  { key: "food", label: "Food", full: "Food & Groceries", Icon: ShoppingCart },
  { key: "transportation", label: "Transport", full: "Transportation", Icon: Car },
  { key: "insurance", label: "Insurance", full: "Insurance", Icon: ShieldCheck },
  { key: "healthcare", label: "Health", full: "Healthcare", Icon: HeartPulse },
  { key: "debt", label: "Debt", full: "Debt Payments", Icon: CreditCard },
  { key: "savings", label: "Savings", full: "Savings", Icon: PiggyBank },
  { key: "entertainment", label: "Fun", full: "Entertainment", Icon: Popcorn },
  { key: "personal_care", label: "Personal", full: "Personal Care", Icon: Sparkles },
  { key: "children_pets", label: "Kids/Pets", full: "Children & Pets", Icon: PawPrint },
  { key: "miscellaneous", label: "Misc", full: "Miscellaneous", Icon: Package },
];

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const diffColor = (d) => (Number(d) >= 0 ? "text-emerald-400" : "text-rose-400");

function ExpenseRow({ weekIdx, catKey, label, title, Icon, expense, custom, onChange, onRemove }) {
  const e = expense || { amount: 0, budget: 0, due_date: null, reminder_active: false, category_type: "personal" };
  const upd = (patch) => onChange({ ...e, ...patch });
  const isBiz = e.category_type === "business";
  const diff = (Number(e.budget) || 0) - (Number(e.amount) || 0);
  return (
    <div className="flex flex-col gap-1 min-w-0" data-testid={`snap-w${weekIdx}-row-${catKey}`}>
      {/* one row — name · category · bell · P/B · Budget · Actual · Δ · delete */}
      <div className="flex flex-wrap items-center gap-1 text-xs min-w-0">
        <Icon className={`w-3.5 h-3.5 shrink-0 ${isBiz ? "text-[#FFC43D]" : "text-sky-400"}`} />
        {custom ? (
          <input
            value={label}
            onChange={(ev) => onChange({ ...e, label: ev.target.value })}
            placeholder="Item name"
            data-testid={`snap-w${weekIdx}-custom-label-${catKey}`}
            className="bg-transparent text-slate-300 flex-1 min-w-[40px] focus:outline-none border-b border-transparent focus:border-emerald-500/40"
          />
        ) : (
          <span title={title || label} className="text-slate-400 flex-1 min-w-[40px] truncate leading-tight">{label}</span>
        )}
        {isBiz && (
          <select value={e.biz_category || ""} onChange={(ev) => upd({ biz_category: ev.target.value })} data-testid={`snap-w${weekIdx}-bizcat-${catKey}`} title="Business (IRS) category" className="shrink min-w-0 max-w-[70px] bg-slate-900/70 border border-[#FFC43D]/40 text-[10px] text-[#FFE08A] rounded px-1 py-0.5 focus:outline-none focus:border-[#FFC43D]">
            <option value="">Cat…</option>
            {BUSINESS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <button onClick={() => upd({ reminder_active: !e.reminder_active })} data-testid={`snap-w${weekIdx}-bell-${catKey}`} title="Toggle due-date alert" className={`shrink-0 transition-colors ${e.reminder_active ? "text-amber-400" : "text-slate-600 hover:text-slate-400"}`}>
          {e.reminder_active ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
        </button>
        <div className="inline-flex shrink-0 rounded overflow-hidden border border-white/10" data-testid={`snap-w${weekIdx}-type-${catKey}`}>
          <button onClick={() => upd({ category_type: "personal" })} data-testid={`snap-w${weekIdx}-type-personal-${catKey}`} title="Personal expense" className={`px-1 py-1 text-[10px] font-bold leading-none transition-colors ${!isBiz ? "bg-sky-500 text-white" : "text-slate-500 hover:text-slate-300"}`}>P</button>
          <button onClick={() => upd({ category_type: "business" })} data-testid={`snap-w${weekIdx}-type-business-${catKey}`} title="Business expense" className={`px-1 py-1 text-[10px] font-bold leading-none transition-colors ${isBiz ? "bg-[#FFC43D] text-[#1a1400]" : "text-slate-500 hover:text-slate-300"}`}>B</button>
        </div>
        <div className="flex items-center gap-1 ml-auto shrink-0">
          <input type="number" value={e.budget || ""} placeholder="Bgt" title="Budget" onChange={(ev) => upd({ budget: parseFloat(ev.target.value) || 0 })} data-testid={`snap-w${weekIdx}-budget-${catKey}`} className="bg-slate-900/70 border border-white/15 text-right px-1 py-0.5 rounded w-[44px] text-xs font-mono text-slate-300 focus:outline-none focus:border-slate-400 shrink-0" />
          <input type="number" value={e.amount || ""} placeholder="Act" title="Actual" onChange={(ev) => upd({ amount: parseFloat(ev.target.value) || 0 })} data-testid={`snap-w${weekIdx}-amount-${catKey}`} className={`bg-slate-900/70 border text-right px-1 py-0.5 rounded w-[44px] text-xs font-mono focus:outline-none shrink-0 ${isBiz ? "border-[#FFC43D]/50 text-[#FFE08A] focus:border-[#FFC43D]" : "border-sky-500/40 text-sky-200 focus:border-sky-400"}`} />
          <span title="Budget − Actual" data-testid={`snap-w${weekIdx}-diff-${catKey}`} className={`w-[50px] text-right font-mono text-[11px] shrink-0 ${diffColor(diff)}`}>{money(diff)}</span>
        </div>
        {custom && (
          <button onClick={onRemove} data-testid={`snap-w${weekIdx}-custom-remove-${catKey}`} className="shrink-0 text-slate-600 hover:text-rose-400 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {e.reminder_active && (
        <input
          type="date"
          value={e.due_date || ""}
          onChange={(ev) => upd({ due_date: ev.target.value || null })}
          data-testid={`snap-w${weekIdx}-due-${catKey}`}
          className="ml-5 bg-slate-900/70 border border-amber-500/30 text-[11px] text-amber-200 px-1.5 py-0.5 rounded focus:outline-none focus:border-amber-400"
        />
      )}
    </div>
  );
}

function WeekColumn({ week, idx, onChange, sourceAmounts }) {
  const wn = idx + 1;
  const income = (week.paychecks || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const incomeBudget = (week.paychecks || []).reduce((s, p) => s + (Number(p.budget) || 0), 0);
  let expenses = CATS.reduce((s, c) => s + (week.expenses?.[c.key]?.category_type === "business" ? 0 : (Number(week.expenses?.[c.key]?.amount) || 0)), 0);
  expenses += (week.custom || []).reduce((s, x) => s + (x.category_type === "business" ? 0 : (Number(x.amount) || 0)), 0);
  let expensesBudget = CATS.reduce((s, c) => s + (week.expenses?.[c.key]?.category_type === "business" ? 0 : (Number(week.expenses?.[c.key]?.budget) || 0)), 0);
  expensesBudget += (week.custom || []).reduce((s, x) => s + (x.category_type === "business" ? 0 : (Number(x.budget) || 0)), 0);
  const net = income - expenses;
  const netBudget = incomeBudget - expensesBudget;
  const payCount = (week.paychecks || []).filter((p) => Number(p.amount) > 0).length;
  const receivedCount = (week.paychecks || []).filter((p) => p.is_received && Number(p.amount) > 0).length;

  const setPaycheck = (i, patch) => {
    const paychecks = week.paychecks.map((p, j) => (j === i ? { ...p, ...patch } : p));
    onChange({ ...week, paychecks });
  };
  const addPaycheck = () => {
    if ((week.paychecks || []).length >= 3) return;
    const id = (week.paychecks?.length || 0) + 1;
    onChange({ ...week, paychecks: [...(week.paychecks || []), { id, name: `Pay ${id}`, amount: 0, budget: 0, is_received: false }] });
  };
  const setExpense = (key, val) => onChange({ ...week, expenses: { ...week.expenses, [key]: val } });
  const removePaycheck = (i) => onChange({ ...week, paychecks: week.paychecks.filter((_, j) => j !== i) });
  const setCustom = (i, val) => onChange({ ...week, custom: week.custom.map((x, j) => (j === i ? val : x)) });
  const addCustom = () => {
    if ((week.custom || []).length >= 5) return;
    onChange({ ...week, custom: [...(week.custom || []), { id: Date.now(), label: "", amount: 0, budget: 0, due_date: null, reminder_active: false }] });
  };
  const removeCustom = (i) => onChange({ ...week, custom: week.custom.filter((_, j) => j !== i) });

  return (
    <div data-testid={`snap-week-${wn}`} className="bg-[#0a1226]/80 rounded-xl p-4 border border-white/8 flex flex-col">
      <h3 className="text-sm font-bold text-slate-200 border-b border-white/10 pb-2 mb-3 flex items-center justify-between">
        <span>Week {wn}</span>
        <span data-testid={`snap-week-${wn}-net`} className={`font-mono text-xs ${net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(net)}</span>
      </h3>

      <div data-onboard={wn === 1 ? "paychecks" : undefined} className="space-y-1.5 mb-4">
        <label className="text-[10px] font-bold tracking-wider uppercase text-emerald-500">Expected Net Income</label>
        {(week.paychecks || []).map((p, i) => (
          <div key={p.id} className="bg-slate-900/60 p-1.5 rounded-lg border border-white/10 space-y-1">
            <input
              type="text"
              value={p.name || ""}
              placeholder="Employer / source"
              list="snap-income-sources"
              onChange={(e) => {
                const name = e.target.value;
                const patch = { name };
                const amt = sourceAmounts?.[name];
                if (amt && !(Number(p.amount) > 0)) patch.amount = amt;
                setPaycheck(i, patch);
              }}
              data-testid={`snap-w${wn}-pay-name-${i}`}
              className="bg-transparent text-[11px] w-full focus:outline-none text-slate-300"
            />
            <div className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={!!p.is_received}
                onChange={(e) => { setPaycheck(i, { is_received: e.target.checked }); logEvent("paycheck_toggle", { week: wn, received: e.target.checked }); }}
                data-testid={`snap-w${wn}-pay-received-${i}`}
                title="Mark as received"
                className="rounded accent-emerald-500 w-3.5 h-3.5 shrink-0"
              />
              <input
                type="number"
                value={p.budget || ""}
                placeholder="Bgt"
                title="Budgeted income"
                onChange={(e) => setPaycheck(i, { budget: parseFloat(e.target.value) || 0 })}
                data-testid={`snap-w${wn}-pay-budget-${i}`}
                className="bg-slate-900/60 border border-white/15 rounded text-[11px] w-[46px] px-1 py-0.5 focus:outline-none font-mono text-slate-300 shrink-0 text-right"
              />
              <span className="text-emerald-500 text-xs shrink-0">$</span>
              <input
                type="number"
                value={p.amount || ""}
                placeholder="Act"
                onChange={(e) => setPaycheck(i, { amount: parseFloat(e.target.value) || 0 })}
                data-testid={`snap-w${wn}-pay-amount-${i}`}
                className="bg-transparent text-xs w-[46px] focus:outline-none font-mono text-emerald-300 text-right"
              />
              <span title="Budget − Actual" data-testid={`snap-w${wn}-pay-diff-${i}`} className={`font-mono text-[11px] ml-auto shrink-0 ${diffColor((Number(p.budget) || 0) - (Number(p.amount) || 0))}`}>{money((Number(p.budget) || 0) - (Number(p.amount) || 0))}</span>
              {i > 0 && (
                <button onClick={() => removePaycheck(i)} data-testid={`snap-w${wn}-pay-remove-${i}`} className="shrink-0 text-slate-600 hover:text-rose-400 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        {(week.paychecks || []).length < 3 && (
          <button onClick={addPaycheck} data-testid={`snap-w${wn}-add-pay`} className="text-[11px] text-emerald-400/80 hover:text-emerald-300 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add pay
          </button>
        )}
      </div>

      <div data-onboard={wn === 1 ? "expenses" : undefined} className="space-y-2.5">
        <label className="text-[10px] font-bold tracking-wider uppercase text-slate-500">Expenses</label>
        {CATS.map(({ key, label, full, Icon }) => (
          <ExpenseRow
            key={key} weekIdx={wn} catKey={key} label={label} title={full} Icon={Icon}
            expense={week.expenses?.[key]}
            onChange={(val) => setExpense(key, val)}
          />
        ))}
        {(week.custom || []).map((x, i) => (
          <ExpenseRow
            key={x.id} weekIdx={wn} catKey={`custom-${i}`} label={x.label} Icon={Star} custom
            expense={x}
            onChange={(val) => setCustom(i, val)}
            onRemove={() => removeCustom(i)}
          />
        ))}
        {(week.custom || []).length < 5 && (
          <button onClick={addCustom} data-testid={`snap-w${wn}-add-custom`} className="text-[11px] text-slate-400 hover:text-emerald-300 flex items-center gap-1 mt-1">
            <Plus className="w-3 h-3" /> Add custom item
          </button>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-white/10 space-y-1.5">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 items-center text-[11px]">
          <span></span>
          <span className="text-[9px] uppercase text-slate-600 text-right w-[50px]">Budget</span>
          <span className="text-[9px] uppercase text-slate-600 text-right w-[50px]">Actual</span>
          <span className="text-[9px] uppercase text-slate-600 text-right w-[50px]">Δ</span>
          <span className="text-slate-500">Income</span>
          <span className="font-mono text-right text-slate-400">{money(incomeBudget)}</span>
          <span className="font-mono text-right text-emerald-400">{money(income)}</span>
          <span className={`font-mono text-right ${diffColor(incomeBudget - income)}`}>{money(incomeBudget - income)}</span>
          <span className="text-slate-500">Expenses</span>
          <span className="font-mono text-right text-slate-400">{money(expensesBudget)}</span>
          <span className="font-mono text-right text-rose-400">{money(expenses)}</span>
          <span className={`font-mono text-right ${diffColor(expensesBudget - expenses)}`}>{money(expensesBudget - expenses)}</span>
          <span className="text-slate-300 font-semibold pt-1">Net Fluid</span>
          <span className="font-mono text-right text-slate-300 pt-1">{money(netBudget)}</span>
          <span data-testid={`snap-week-${wn}-net-actual`} className={`font-mono text-right pt-1 ${net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(net)}</span>
          <span data-testid={`snap-week-${wn}-diff`} className={`font-mono text-right pt-1 ${diffColor(netBudget - net)}`}>{money(netBudget - net)}</span>
        </div>
        <div className="pt-0.5">
          <div className="flex justify-between text-[10px] text-slate-600"><span>received</span><span data-testid={`snap-week-${wn}-received`} className="font-mono">{receivedCount}/{payCount}</span></div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-0.5"><div className={`h-full rounded-full transition-all duration-500 ${payCount > 0 && receivedCount >= payCount ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${payCount ? Math.round((receivedCount / payCount) * 100) : 0}%` }} /></div>
        </div>
      </div>
    </div>
  );
}

export default function BudgetMatrix({ budget, onChange }) {
  const [weeks, setWeeks] = useState(budget?.weeks || []);
  const [sources, setSources] = useState([]);
  useEffect(() => { api.get("/snapbudget/sources").then((r) => setSources(r.data.sources || [])).catch(() => {}); }, [budget?.month, budget?.year]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setWeeks(budget?.weeks || []); }, [budget?.month, budget?.year]);

  const updateWeek = (idx, w) => {
    const next = weeks.map((x, i) => (i === idx ? w : x));
    setWeeks(next);
    onChange(next);
  };

  const mTotals = weeks.reduce((a, w) => {
    const incA = (w.paychecks || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const incB = (w.paychecks || []).reduce((s, p) => s + (Number(p.budget) || 0), 0);
    let expA = CATS.reduce((s, c) => s + (w.expenses?.[c.key]?.category_type === "business" ? 0 : (Number(w.expenses?.[c.key]?.amount) || 0)), 0);
    expA += (w.custom || []).reduce((s, x) => s + (x.category_type === "business" ? 0 : (Number(x.amount) || 0)), 0);
    let expB = CATS.reduce((s, c) => s + (w.expenses?.[c.key]?.category_type === "business" ? 0 : (Number(w.expenses?.[c.key]?.budget) || 0)), 0);
    expB += (w.custom || []).reduce((s, x) => s + (x.category_type === "business" ? 0 : (Number(x.budget) || 0)), 0);
    return { actual: a.actual + (incA - expA), budget: a.budget + (incB - expB) };
  }, { actual: 0, budget: 0 });
  const monthNet = mTotals.actual;
  const monthNetBudget = mTotals.budget;

  const catAlerts = CATS.map((c) => {
    let a = 0, b = 0;
    weeks.forEach((w) => { const e = w.expenses?.[c.key]; if (e && e.category_type !== "business") { a += Number(e.amount) || 0; b += Number(e.budget) || 0; } });
    return { label: c.label, actual: a, budget: b };
  }).filter((x) => x.budget > 0);
  const mileage = budget?.mileage_credit;

  const sourceAmounts = Object.fromEntries(sources.map((s) => [s.name, s.amount]));

  return (
    <div className="w-full bg-[#0d1730] border border-emerald-500/20 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-emerald-950/40 flex flex-col">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-5 border-b border-white/10 pb-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            <img src="/snapbudget-logo.webp" alt="" className="w-7 h-7 rounded-md" />
            SnapBudget
            <span className="text-[10px] font-normal px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">Premium</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">4-Week Cashflow Balance Matrix</p>
        </div>
        <div data-onboard="summary" className="bg-slate-900/60 border border-white/10 rounded-xl px-4 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Real-Time Profit Net</p>
          <div className="flex items-end gap-4 justify-end mt-0.5">
            <div className="text-right"><p className="text-[9px] uppercase text-slate-600">Total Budget</p><p data-testid="snap-month-budget" className="font-mono text-sm font-bold text-slate-300">{money(monthNetBudget)}</p></div>
            <div className="text-right"><p className="text-[9px] uppercase text-slate-600">Total Actual</p><p data-testid="snap-month-net" className={`font-mono text-lg font-bold ${monthNet >= 0 ? "text-emerald-400 cc-gold-positive" : "text-rose-400"}`}>{money(monthNet)}</p></div>
            <div className="text-right"><p className="text-[9px] uppercase text-slate-600">Total Δ</p><p data-testid="snap-month-diff" className={`font-mono text-sm font-bold ${diffColor(monthNetBudget - monthNet)}`}>{money(monthNetBudget - monthNet)}</p></div>
          </div>
          {typeof budget?.rollover === "number" && budget.rollover !== 0 && (
            <p data-testid="snap-rollover" className="text-[10px] text-slate-400 mt-0.5 text-right">
              incl. rollover <span className={budget.rollover >= 0 ? "text-emerald-400" : "text-rose-400"}>{money(budget.rollover)}</span> → avail <span className="text-emerald-300 font-mono">{money(monthNet + budget.rollover)}</span>
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {weeks.map((w, i) => <WeekColumn key={i} week={w} idx={i} sourceAmounts={sourceAmounts} onChange={(nw) => updateWeek(i, nw)} />)}
      </div>
      {(mileage?.deduction > 0 || catAlerts.length > 0) && (
        <div className="mt-5 space-y-2" data-testid="snap-budget-alerts">
          {mileage?.deduction > 0 && (
            <div data-testid="snap-mileage-credit" className="flex items-center justify-between text-sm rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-2">
              <span className="text-slate-300">Mileage tax credit · {mileage.miles} mi × ${mileage.rate}/mi</span>
              <span className="font-mono text-emerald-400">+{money(mileage.deduction)}</span>
            </div>
          )}
          {catAlerts.map((c) => {
            const over = c.actual > c.budget;
            return (
              <div key={c.label} data-testid={`snap-alert-${c.label}`} className={`flex items-center justify-between text-xs rounded px-3 py-1.5 border ${over ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/20 bg-emerald-500/5"}`}>
                <span className="text-slate-400">{c.label} · {money(c.actual)} of {money(c.budget)}</span>
                <span className={`font-mono ${over ? "text-rose-400" : "text-emerald-400"}`}>{over ? `over by ${money(c.actual - c.budget)}` : `${money(c.budget - c.actual)} left`}</span>
              </div>
            );
          })}
        </div>
      )}
      <datalist id="snap-income-sources">
        {sources.map((s) => <option key={s.name} value={s.name} />)}
      </datalist>
    </div>
  );
}
